import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Upload, UserPlus } from 'lucide-react';
import { footerApi } from '../../utils/api';
import { refreshSiteSettings } from '../../hooks/useSiteSettings';

// हाम्रोबारे page management — text + team members (photo, name, role, phone, email)
function AdminAbout() {
  const [aboutText, setAboutText] = useState('');
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await footerApi.getSettings();
      const data = res.data || {};
      setAboutText(data.about_text || '');
      try {
        const arr = JSON.parse(data.team_members || '[]');
        setTeam(
          Array.isArray(arr)
            ? arr.map((m) => ({
                name: m?.name || '',
                role: m?.role || '',
                phone: m?.phone || '',
                email: m?.email || '',
                photo: m?.photo || '',
                photoFile: null,
              }))
            : []
        );
      } catch {
        setTeam([]);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateMember = (i, field, value) =>
    setTeam((t) => t.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));

  const handlePhotoChange = (i, file) => {
    if (!file) return;
    setTeam((t) =>
      t.map((m, idx) =>
        idx === i ? { ...m, photoFile: file, previewUrl: URL.createObjectURL(file) } : m
      )
    );
  };

  const removeMemberPhoto = (i) =>
    setTeam((t) => t.map((m, idx) => (idx === i ? { ...m, photoFile: null, photo: '', previewUrl: '' } : m)));

  const addMember = () =>
    setTeam([...team, { name: '', role: '', phone: '', email: '', photo: '', photoFile: null, previewUrl: '' }]);

  const removeMember = (i) => setTeam(team.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const membersOut = [];
      const idxList = [];
      const files = [];
      team.forEach((m, i) => {
        membersOut.push({
          name: m.name,
          role: m.role,
          phone: m.phone,
          email: m.email,
          photo: m.photoFile instanceof File ? '' : m.photo,
        });
        if (m.photoFile instanceof File) {
          idxList.push(i);
          files.push(m.photoFile);
        }
      });

      await footerApi.updateSettings(
        {
          about_text: aboutText,
          team_members: JSON.stringify(membersOut),
          team_photo_indexes: JSON.stringify(idxList),
        },
        { teamPhotos: files }
      );
      refreshSiteSettings();
      toast.success('हाम्रोबारे पृष्ठ अद्यावधिक भयो।');
      await fetchSettings();
    } catch (error) {
      console.error('Error saving about settings:', error);
      toast.error(error.response?.data?.error || 'सुरक्षित गर्न असफल भयो।');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm outline-none bg-white focus:border-crimson';
  const fileToUrl = (m) => (m.previewUrl ? m.previewUrl : m.photo);

  return (
    <div>
      <h2 className="font-display text-2xl mb-5">हाम्रोबारे व्यवस्थापन</h2>

      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6">
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft mb-5">
          हाम्रोबारे संक्षिप्त पाठ
          <textarea
            rows={3}
            className={`${inputCls} resize-y font-normal`}
            value={aboutText}
            onChange={(e) => setAboutText(e.target.value)}
            placeholder="निष्पक्ष, विश्वसनीय र समयमै — नेपालको मुटुबाट।"
          />
        </label>

        <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink-soft mb-3">
          <UserPlus size={15} /> टिम सदस्यहरू ({team.length})
        </span>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {team.map((m, i) => (
            <div key={i} className="border border-line rounded-xl p-4 bg-paper-soft/40">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {fileToUrl(m) ? (
                    <img src={fileToUrl(m)} alt={m.name || `member-${i}`} className="w-16 h-16 rounded-full object-cover border border-line" />
                  ) : (
                    <span className="w-16 h-16 rounded-full bg-paper-tint border border-dashed border-line flex items-center justify-center text-muted text-xl font-bold">
                      {(m.name || '?').trim().charAt(0)}
                    </span>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-2.5 py-1.5 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
                      <Upload size={13} /> फोटो
                      <input
                        id={`member-photo-${i}`}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handlePhotoChange(i, e.target.files?.[0])}
                      />
                    </label>
                    {(m.photoFile || m.photo) && (
                      <button type="button" className="text-[11.5px] font-semibold text-crimson hover:underline w-fit" onClick={() => removeMemberPhoto(i)}>
                        फोटो हटाउनुहोस्
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-red-50 hover:text-crimson"
                  onClick={() => removeMember(i)}
                  aria-label="Remove member"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="grid gap-2.5 grid-cols-1 sm:grid-cols-2">
                <input className={inputCls} value={m.name} onChange={(e) => updateMember(i, 'name', e.target.value)} placeholder="नाम *" />
                <input className={inputCls} value={m.role} onChange={(e) => updateMember(i, 'role', e.target.value)} placeholder="पद / Role" />
                <input className={inputCls} value={m.phone} onChange={(e) => updateMember(i, 'phone', e.target.value)} placeholder="फोन (clickable)" />
                <input className={inputCls} value={m.email} onChange={(e) => updateMember(i, 'email', e.target.value)} placeholder="इमेल (clickable)" />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addMember}
            className="min-h-[120px] rounded-xl border-2 border-dashed border-line text-muted flex items-center justify-center gap-2 text-[13px] font-semibold hover:border-crimson hover:text-crimson"
          >
            <Plus size={18} /> टिम सदस्य थप्नुहोस्
          </button>
        </div>

        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-bold text-[13.5px] px-5 py-2.5 rounded-full mt-5 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={saving}
        >
          <Save size={15} /> सुरक्षित गर्नुहोस्
        </button>
      </div>
    </div>
  );
}

export default AdminAbout;
